/**
 * The Doppler effect.
 *
 * Sound uses the classical formula f' = f × (v + vₒ) ÷ (v − vₛ) for a medium (air) at
 * rest, where vₒ is the observer's speed toward the source and vₛ the source's speed
 * toward the observer; a negative value means moving away. Light needs no medium and
 * obeys the relativistic formula, 1 + z = √((1 + β) ÷ (1 − β)) for recession at β = v/c.
 */

import { SPEED_OF_LIGHT } from "@/lib/tool-kit/physics-waves/electromagnetic";

export type SoundError = "supersonic" | "observer-outruns";

export type SoundResult =
  | { ok: true; frequency: number }
  | { ok: false; error: SoundError };

function check(v: number, observer: number, source: number): SoundError | null {
  if (source >= v) {
    return "supersonic";
  }
  if (v + observer <= 0) {
    return "observer-outruns";
  }
  return null;
}

/** Frequency heard: f × (v + vₒ) ÷ (v − vₛ). Speeds are toward the other party. */
export function observedFrequency(
  source: number,
  v: number,
  observer: number,
  sourceSpeed: number
): SoundResult {
  const error = check(v, observer, sourceSpeed);
  if (error !== null) {
    return { ok: false, error };
  }
  return { ok: true, frequency: (source * (v + observer)) / (v - sourceSpeed) };
}

/** The frequency the source emits, given the frequency heard. */
export function emittedFrequency(
  heard: number,
  v: number,
  observer: number,
  sourceSpeed: number
): SoundResult {
  const error = check(v, observer, sourceSpeed);
  if (error !== null) {
    return { ok: false, error };
  }
  return { ok: true, frequency: (heard * (v - sourceSpeed)) / (v + observer) };
}

/**
 * The source's speed toward the observer (negative when receding) from the emitted and
 * heard frequencies: vₛ = v − f × (v + vₒ) ÷ f'. Null when a frequency is not positive
 * or the observer outruns the sound.
 */
export function sourceSpeedFromShift(
  source: number,
  heard: number,
  v: number,
  observer: number
): number | null {
  if (!(source > 0 && heard > 0 && v + observer > 0)) {
    return null;
  }
  return v - (source * (v + observer)) / heard;
}

/** Mach number of a speed in a medium with sound speed v. */
export function mach(speed: number, v: number): number {
  return speed / v;
}

export type LightError = "too-fast" | "invalid-z";

export type LightResult =
  | { ok: true; z: number; beta: number }
  | { ok: false; error: LightError };

/**
 * Redshift from β = v/c, positive for a receding source and negative for an approaching
 * one: z = √((1 + β) ÷ (1 − β)) − 1.
 */
export function redshiftFromBeta(beta: number): LightResult {
  if (!(Number.isFinite(beta) && Math.abs(beta) < 1)) {
    return { ok: false, error: "too-fast" };
  }
  return { ok: true, z: Math.sqrt((1 + beta) / (1 - beta)) - 1, beta };
}

/** β = ((1 + z)² − 1) ÷ ((1 + z)² + 1), from a redshift z > −1. */
export function betaFromRedshift(z: number): LightResult {
  if (!(Number.isFinite(z) && z > -1)) {
    return { ok: false, error: "invalid-z" };
  }
  const square = (1 + z) ** 2;
  return { ok: true, z, beta: (square - 1) / (square + 1) };
}

/** z = λ_observed ÷ λ_emitted − 1. */
export function redshiftFromWavelengths(
  observed: number,
  emitted: number
): number | null {
  if (!(observed > 0 && emitted > 0)) {
    return null;
  }
  return observed / emitted - 1;
}

export type LightGoal = "speed" | "z" | "wavelengths";

export interface LightInput {
  /** Speed in m/s, unsigned. */
  speed: number | null;
  receding: boolean;
  z: number | null;
  observed: number | null;
  emitted: number | null;
}

/** Solve for z and β from whichever inputs the chosen goal uses; "missing" while any is blank. */
export function solveLight(
  goal: LightGoal,
  input: LightInput
): LightResult | "missing" {
  if (goal === "speed") {
    if (input.speed === null) {
      return "missing";
    }
    const beta =
      (input.receding ? 1 : -1) * (Math.abs(input.speed) / SPEED_OF_LIGHT);
    return redshiftFromBeta(beta);
  }
  if (goal === "z") {
    return input.z === null ? "missing" : betaFromRedshift(input.z);
  }
  if (input.observed === null || input.emitted === null) {
    return "missing";
  }
  const z = redshiftFromWavelengths(input.observed, input.emitted);
  return z === null ? { ok: false, error: "invalid-z" } : betaFromRedshift(z);
}
